import { supabase } from './supabase';

interface ErrorLogOptions {
  errorType: string;
  errorMessage: string;
  stackTrace?: string;
  componentStack?: string;
  sessionId?: string;
}

interface GenerationErrorLogOptions {
  generationType: 'blog_idea' | 'blog_title' | 'blog_intro' | 'blog_content' | 'blog_image' | 'campaign_email' | 'campaign_variant';
  errorCategory: 'OPENAI_UNAVAILABLE' | 'TIMEOUT' | 'NETWORK_ERROR' | 'JAVASCRIPT_ERROR' | 'VALIDATION_ERROR' | 'API_ERROR' | 'UNKNOWN';
  errorMessage: string;
  technicalError?: string;
  retryAttempt?: number;
  blogId?: string;
  campaignId?: string;
  sessionId?: string;
  additionalContext?: {
    service?: 'openai' | 'leonardo';
    apiType?: string;
    timeout?: number;
    lastError?: string;
    [key: string]: any;
  };
}

export async function logError(options: ErrorLogOptions): Promise<void> {
  try {
    const screenName = window.location.pathname;
    const userAgent = navigator.userAgent;

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-user-error`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'apikey': (import.meta.env.VITE_SUPABASE_ANON_KEY),
        },
        body: JSON.stringify({
          error_type: options.errorType,
          error_message: options.errorMessage,
          stack_trace: options.stackTrace,
          component_stack: options.componentStack,
          screen_name: screenName,
          user_agent: userAgent,
          session_id: options.sessionId,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to log error to backend:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
    }
  } catch (error) {
    console.error('Exception in errorLogger:', error);
  }
}

export async function logGenerationError(options: GenerationErrorLogOptions): Promise<void> {
  try {
    const screenName = window.location.pathname;
    const userAgent = navigator.userAgent;

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const technicalDetails: Record<string, any> = {
      url: window.location.href,
      userAgent,
      timestamp: new Date().toISOString(),
      ...options.additionalContext,
    };

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-user-error`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'apikey': (import.meta.env.VITE_SUPABASE_ANON_KEY),
        },
        body: JSON.stringify({
          error_type: 'generation_error',
          error_message: options.errorMessage,
          stack_trace: options.technicalError,
          screen_name: screenName,
          user_agent: userAgent,
          generation_type: options.generationType,
          error_category: options.errorCategory,
          retry_attempt: options.retryAttempt || 0,
          blog_id: options.blogId,
          campaign_id: options.campaignId,
          session_id: options.sessionId,
          technical_details: technicalDetails,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try {
        errorDetails = JSON.parse(errorText);
      } catch {
        errorDetails = errorText;
      }
      console.error('Failed to log generation error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
        details: errorDetails
      });
    }
  } catch (error) {
    console.error('Exception in logGenerationError:', error);
  }
}

export function wrapAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  errorType: string = 'async_error'
): T {
  return (async (...args: any[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const stackTrace = error instanceof Error ? error.stack : undefined;

      await logError({
        errorType,
        errorMessage,
        stackTrace,
      });

      throw error;
    }
  }) as T;
}

export async function resolveTimeoutErrors(sessionId: string): Promise<void> {
  try {
    const { data: errors, error: fetchError } = await supabase
      .from('user_errors')
      .select('id, technical_details')
      .eq('session_id', sessionId)
      .eq('generation_type', 'blog_content')
      .eq('error_category', 'TIMEOUT')
      .is('resolved_at', null);

    if (fetchError) {
      console.error('[resolveTimeoutErrors] Error fetching timeout errors:', fetchError);
      return;
    }

    if (!errors || errors.length === 0) {
      return;
    }

    console.log(`[resolveTimeoutErrors] Found ${errors.length} unresolved timeout error(s) for session ${sessionId}`);

    for (const error of errors) {
      const updatedTechnicalDetails = {
        ...error.technical_details,
        auto_resolved: true,
        auto_resolved_at: new Date().toISOString(),
        resolution_reason: 'content_generated_after_timeout'
      };

      const { error: updateError } = await supabase
        .from('user_errors')
        .update({
          resolved_at: new Date().toISOString(),
          technical_details: updatedTechnicalDetails
        })
        .eq('id', error.id);

      if (updateError) {
        console.error('[resolveTimeoutErrors] Error updating error record:', updateError);
      } else {
        console.log(`[resolveTimeoutErrors] Auto-resolved timeout error ${error.id}`);
      }
    }
  } catch (error) {
    console.error('[resolveTimeoutErrors] Exception:', error);
  }
}

export function wrapSync<T extends (...args: any[]) => any>(
  fn: T,
  errorType: string = 'sync_error'
): T {
  return ((...args: any[]) => {
    try {
      return fn(...args);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const stackTrace = error instanceof Error ? error.stack : undefined;

      logError({
        errorType,
        errorMessage,
        stackTrace,
      }).catch(console.error);

      throw error;
    }
  }) as T;
}
